class SizeChartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // Close drawer when clicking on the overlay or on the drawer background
    this.addEventListener('click', this.handleClick.bind(this));
  }

  connectedCallback() {
    const openSizeChartDrawerButton = document.getElementById('openSizeChartDrawer');
    if (openSizeChartDrawerButton) {
      openSizeChartDrawerButton.addEventListener('click', () => {
        this.open();
      });
    }

    const regularFitButton = document.getElementById('regularFitButton');
    if (regularFitButton) {
      regularFitButton.addEventListener('click', () => {
        this.open();
      });
    }

    const closeSizeChartDrawerButton = this.querySelector('.drawer__close');
    if (closeSizeChartDrawerButton) {
      closeSizeChartDrawerButton.addEventListener('click', () => {
        this.close();
      });
    }

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && this.classList.contains('active')) {
        this.close();
      }
    });

    // Send the current fit info to the iframe
    // Prepare iframe messaging for current fit
    this._iframe = this.querySelector('iframe');
    // Dynamically set iframe src after verifying endpoint exists to avoid visible 404 responses
    if (this._iframe && this._iframe.dataset.src) {
      const potentialSrc = this._iframe.dataset.src;
      // Perform a lightweight HEAD request to verify the endpoint exists
      fetch(potentialSrc, { method: 'HEAD' })
        .then((response) => {
          if (response.ok) {
            // Endpoint available, set src to load content
            this._iframe.src = potentialSrc;
          } else {
            // Hide iframe container to prevent 404 flash
            this._iframe.style.display = 'none';
          }
        })
        .catch(() => {
          // Network error, hide iframe
          this._iframe.style.display = 'none';
        });
    }
    this._currentFit = (this.dataset.currentFit || '').toString().trim();

    const sendCurrentFitToIframe = () => {
      if (!this._iframe || !this._iframe.contentWindow || !this._currentFit) return;
      try {
        const targetOrigin = new URL(this._iframe.src, window.location.href).origin;
        this._iframe.contentWindow.postMessage(
          { type: 'SIZE_CHART_SET_CURRENT_FIT', currentFit: this._currentFit },
          targetOrigin,
        );
      } catch (error) {
        // noop
      }
    };

    // Send once iframe loads
    if (this._iframe) {
      this._iframe.addEventListener('load', sendCurrentFitToIframe);
    }

    // Store sender for reuse in open()
    this._sendCurrentFitToIframe = sendCurrentFitToIframe;
  }

  handleClick(event) {
    if (
      event.target.id === 'SizeChartDrawer-Overlay' ||
      event.target.classList?.contains('details-drawer__overlay') ||
      event.target === this
    ) {
      this.close();
    }
  }

  open(triggeredBy) {
    console.log('open');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const sizeChartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (sizeChartDrawerNote && !sizeChartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(sizeChartDrawerNote);
    }
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('SizeChartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // After opening, attempt to send current fit to the iframe (in case it's already loaded)
    if (typeof this._sendCurrentFitToIframe === 'function') {
      // Slight delay to ensure contentWindow is ready
      setTimeout(() => this._sendCurrentFitToIframe(), 0);
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(detailsDrawerNote) {
    if (!detailsDrawerNote) return;

    detailsDrawerNote.setAttribute('role', 'button');
    detailsDrawerNote.setAttribute('aria-expanded', 'false');

    if (detailsDrawerNote.nextElementSibling && detailsDrawerNote.nextElementSibling.getAttribute('id')) {
      detailsDrawerNote.setAttribute('aria-controls', detailsDrawerNote.nextElementSibling.id);
    }

    detailsDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (detailsDrawerNote.parentElement) {
      detailsDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      // Only attach overlay click listener if overlay exists
      const overlay = this.querySelector('#SizeChartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'size-chart-drawer',
        selector: '#SizeChartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('size-chart-drawer', SizeChartDrawer);

class SizeChartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'SizeChartDrawer',
        section: 'size-chart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('size-chart-drawer-items', SizeChartDrawerItems);
